import { z } from 'zod'

import { ViaSchema } from '@slite/shared'

import { EFFORTS } from './model-profiles'
import { SuperSource } from './super'

/** @knipignore Public agent-kernel request contract. */
export const AgentKernelFeaturesSchema = z.strictObject({
  useHistory: z.boolean().optional(),
  useSliteAgentDispatch: z.boolean().optional(),
})

/** Request cap on attached screenshots; clients truncate to it. */
export const MAX_ATTACHED_IMAGES = 8
/** Shared budget for a question's encoded images within search's 2 MB JSON body. */
export const IMAGE_REQUEST_BUDGET = 1536 * 1024

const StringListSchema = z.array(z.string())
const SourceListSchema = z.array(z.enum(SuperSource))
// The server validates date syntax.
const DateBoundSchema = z.string().nullable()

/** The `RunFilters` subset of `SuperFilters` (fetch-super.ts) that the run reads. */
const RunFiltersSchema = z.strictObject({
  sourceTypeList: SourceListSchema.optional(),
  excludeSourceTypeList: SourceListSchema.optional(),
  parentIdList: StringListSchema.optional(),
  excludeParentIdList: StringListSchema.optional(),
  updatedAfter: DateBoundSchema.optional(),
  updatedBefore: DateBoundSchema.optional(),
  authorEmails: StringListSchema.optional(),
  reviewState: z.string().optional(),
})

/** Shared request schema for the client and server. */
const AgentKernelRequestBodySchema = z.strictObject({
  /** An empty message resumes a parked thread. */
  question: z.string(),

  /** Content set on the first round. */
  brief: z.string().optional(),
  /** Content sent again when it changes. */
  roundContext: z.string().optional(),

  /** Note the user is viewing; the server resolves its content. */
  visitingNoteId: z.string().min(1).max(64).optional(),
  selectedText: z.string().optional(),
  /** Screenshot data URLs attached to the question; the server describes
   *  them into round context. */
  images: z.array(z.string().min(1)).max(MAX_ATTACHED_IMAGES).optional(),

  threadId: z.string().optional(),
  clearThread: z.boolean().optional(),
  /** Rejects a maintenance reply if the saved revision differs. */
  expectedRevision: z.string().optional(),
  /** Agent for a new thread; continuations use the saved agent. */
  primaryAgent: z.enum(['slite', 'super']).optional(),
  /** How hard this round works; the server picks the agent and models for it. */
  effort: z.enum(EFFORTS).optional(),
  filters: RunFiltersSchema.optional(),
  lang: z.string().optional(),

  /** Enables assistant attribution and features. */
  assistantId: z.string().optional(),
  /** Attribution for API and MCP callers; first-party surfaces name themselves too. */
  via: ViaSchema,
  /** Soft credit target; the hard stop is about 1.5× higher. */
  maxCreditsBudget: z.number().optional(),
  /** Named model profile id (see MODEL_PROFILES), for dev tooling and the
   *  harness; wins over the effort's profile. */
  modelProfile: z.string().optional(),
  features: AgentKernelFeaturesSchema.optional(),
})

/** What the server accepts: bundles deployed before `via` was required (and
 *  older mobile builds) omit it, and are attributed from their origin instead. */
export const AgentKernelAcceptedRequestBodySchema =
  AgentKernelRequestBodySchema.extend({ via: ViaSchema.optional() })

export type AgentKernelFeatures = z.infer<typeof AgentKernelFeaturesSchema>
export type AgentKernelRequestBody = z.infer<
  typeof AgentKernelRequestBodySchema
>
